/**
 * Item-card state machine — Phase 7 of `shippo-listing-discriminator`.
 *
 * Single source of truth for the marketplace item-card `stateId` enum and the
 * pure derivation function that maps `(Listing-discriminator, viewer, lifecycle)`
 * → `ItemCardStateId`. Replaces the two parallel state machines documented in
 * DISCRIMINATORS.md §3.2:
 *   - `lib/build-item-card-view.ts` (web) — local `ItemCardStateId` decl + if/else chain
 *   - `apps/mobile/components/ui/item-card.tsx` (mobile) — local `ItemCardStateId` decl
 *     + reverse-order if/else chain (drift: closed-preorder × owner)
 *
 * Both presentations are now thin adapters over this canonical helper. The
 * narrative `story-cards` `kind` union remains a separate presentation
 * surface (different output space — see `story-context-kind.ts`) but BOTH
 * presentations derive their discriminator from the canonical `Listing` ADT
 * via these helpers — no consumer branches on raw fields independently.
 *
 * **Owner-first precedence** (operator-confirmed, IMPACT §3.G):
 *   1. viewer.isOwner            → "own"   (owner sees own card regardless of lifecycle)
 *   2. lifecycle.isSold          → "sold"  (stock=0 OR auction ended/settled)
 *   3. lifecycle.isPreorderClosed → "preorder" (closed-preorder UI variant)
 *   4. sale-model dispatch on `listing.format` × `listing.type`
 *      - "auction"                                 → "auction"
 *      - "fixed_price_with_offers" / "offers_only" → "offers"
 *      - "fixed_price" + type='preorder'            → "preorder"
 *      - "fixed_price" + lifecycle.isScheduledDrop  → "preorder" (drop-pending UI variant)
 *      - "fixed_price" + standard                   → "buynow"
 *
 * The `offerCount` value is intentionally NOT a state-machine input — it is
 * UI adornment (pill text), not the state itself. Pre-Phase-7 both web and
 * mobile gated `stateId='offers'` on `offerCount >= 1`, which silently broke
 * parity with the story-card `kind='offer'` for fpwo+offerCount=0. Under the
 * canonical helper, an offer-enabled format always reports `stateId='offers'`
 * and the consumer renders count-aware pills downstream.
 *
 * @see packages/contract/src/listing-adt.ts — the canonical `Listing` ADT
 * @see packages/contract/src/story-context-kind.ts — sibling helper for story-cards
 * @see .tondocs/listings/DISCRIMINATORS_HYGIENE_IMPACT.md §3.G, §H Phase 7
 * @see .tondocs/listings/DISCRIMINATORS.md §3.2 — pre-Phase-7 dual state machines
 */

import type { ListingFormat, ListingType } from "./listing-adt";

/**
 * Canonical state enum for item-card presentations. Both web
 * (`lib/build-item-card-view.ts`) and mobile
 * (`apps/mobile/components/ui/item-card.tsx`) import this — no local copies.
 */
export const ITEM_CARD_STATE_IDS = [
  "buynow",
  "auction",
  "offers",
  "preorder",
  "sold",
  "own",
] as const;

export type ItemCardStateId = (typeof ITEM_CARD_STATE_IDS)[number];

/**
 * Discriminator inputs — the minimal slice of a `Listing` needed to pick a
 * stateId. Taking the discriminator-only view (instead of a full `Listing`)
 * lets callers feed view-input shapes (`ListingInput` / `ListingItemCardInput`)
 * that don't carry the boundary-required ADT fields (id, seller_id, currency,
 * shipping, …) — those callers are at the presentation seam, downstream of
 * the ADT parser.
 *
 * If a caller has a parsed `Listing` in hand, pass `{ format: l.format, type: l.type }`.
 */
export type ItemCardListingDiscriminator = {
  format: ListingFormat;
  type: ListingType;
};

export type ItemCardViewer = {
  isOwner: boolean;
};

/**
 * Lifecycle inputs — runtime conditions orthogonal to the sale model. The
 * caller computes these from the raw row (stock count, auction status,
 * scheduled_publish_at). Folded into the canonical helper so both web and
 * mobile apply the same precedence rules.
 */
export type ItemCardLifecycle = {
  /**
   * Listing is no longer purchasable. Non-auction: `quantity <= 0`. Auction:
   * `auction_status IN ('ended', 'settled')`. Preorder-closed is its OWN
   * branch (`isPreorderClosed`) because the UI variant differs (closed-preorder
   * shows a "Pre-orders closed" sub-line rather than the generic sold state).
   */
  isSold: boolean;
  /**
   * Preorder listing whose order window closed (type='preorder' AND
   * `quantity <= 0`). Distinct from `isSold` so the consumer renders the
   * "Pre-orders closed" sub-line and CTA variant.
   */
  isPreorderClosed: boolean;
  /**
   * Listing has `scheduled_publish_at` in the future. Drives the
   * "drop-pending" UI variant on `fixed_price` listings.
   */
  isScheduledDrop: boolean;
};

function unreachableItemCardFormat(x: never): never {
  throw new Error(`unhandled item-card listing format: ${String(x)}`);
}

/**
 * Pure, total function from `(listing-discriminator, viewer, lifecycle)` to
 * `ItemCardStateId`. The single legitimate computation of the item-card
 * state — both web and mobile builders call this; no local if/else chains.
 *
 * Exhaustive over `ListingFormat` — extending `LISTING_FORMATS` without
 * extending the switch fails compile via `unreachableItemCardFormat`.
 *
 * Owner-first precedence — see file header for the full rule.
 */
export function deriveItemCardStateId(
  listing: ItemCardListingDiscriminator,
  viewer: ItemCardViewer,
  lifecycle: ItemCardLifecycle,
): ItemCardStateId {
  // 1. Owner sees own card regardless of lifecycle — eliminates the
  //    pre-Phase-7 web/mobile drift on closed-preorder × owner (web returned
  //    "sold" via the isOutOfStock branch; mobile returned "own").
  if (viewer.isOwner) return "own";

  // 2. Sold / ended — single branch for stock-exhausted and auction-ended.
  if (lifecycle.isSold) return "sold";

  // 3. Closed-preorder — distinct UI variant (kept before format dispatch so
  //    a closed preorder doesn't fall through into "buynow").
  if (lifecycle.isPreorderClosed) return "preorder";

  // 4. Sale-model dispatch — exhaustive over the canonical format union.
  switch (listing.format) {
    case "auction":
      return "auction";
    case "fixed_price_with_offers":
    case "offers_only":
      // Offer-enabled formats always report "offers" — pendingCount becomes
      // UI adornment (pill text), not state. Closes the
      // story-card↔item-card drift on fpwo+offerCount=0 (story-card returned
      // kind='offer', item-card returned stateId='buynow').
      return "offers";
    case "fixed_price": {
      if (listing.type === "preorder") return "preorder";
      if (lifecycle.isScheduledDrop) return "preorder";
      return "buynow";
    }
    default:
      return unreachableItemCardFormat(listing.format);
  }
}
